/**
 * Phase 66 Plan 02 (D-09) — Sectioned sidebar.
 *
 * Five sections per CONTEXT.md D-09:
 *   - Dashboard (Panoramica)
 *   - Operations (Macchine, Provisioning)
 *   - Identity (Utenti, VPN)
 *   - Admin (File Manager, Audit log, Circuiti) — gated by <Can>
 *   - Settings (Impostazioni)
 *
 * Each <NavLink> emits BOTH `data-route="<path>"` (Phase 65 attribute
 * selector contract — navigation.spec.js relies on it) AND
 * `data-testid="nav-<slug>"` (66-TESTID-CONTRACT.md migrate row).
 *
 * Admin section wrapped in <Can permission="files:read"> — admin/super_admin
 * roles get files:read in their default ROLE_PERMISSIONS, viewers / circuit_admins
 * get the section hidden. Backend remains authoritative on each /admin/* endpoint.
 *
 * @see PATTERNS.md §Layout chrome — `<Sidebar>` (replaces index.html lines 19-44
 *      + js/app.js applyAdminNavVisibility lines 155-191)
 */

import { NavLink } from "react-router";
import {
  LayoutDashboard,
  Box,
  Server,
  UserCog,
  Settings,
  Network,
  Files,
  Activity,
  CircuitBoard,
  Copy,
  Zap,
  Users,
} from "lucide-react";
import type { ReactNode } from "react";

import Can from "@/auth/Can";
import type { Permission } from "@/auth/permissions";

type NavItem = {
  to: string;
  label: string;
  testid: string;
  icon: ReactNode;
  /** Optional id retained from Phase 65 for legacy navigation specs. */
  id?: string;
  /**
   * Optional per-item permission gate. When set, the item is wrapped in
   * <Can permission={permission}> in addition to any section-level gate.
   *
   * Widened in Phase 70 from `"admin_users:manage"` literal to the full
   * Permission union so additional admin entries (e.g.,
   * machine_clone_templates:manage) can declare their gate without
   * casting. Backend remains authoritative — UI gate is cosmetic.
   */
  permission?: Permission;
};

const sections: { title: string; items: NavItem[]; admin?: boolean }[] = [
  {
    title: "Dashboard",
    items: [
      {
        to: "/",
        label: "Panoramica",
        testid: "nav-dashboard",
        icon: <LayoutDashboard size={16} aria-hidden="true" />,
      },
    ],
  },
  {
    title: "Operations",
    items: [
      {
        to: "/machines",
        label: "Macchine",
        testid: "nav-machines",
        icon: <Box size={16} aria-hidden="true" />,
      },
      // Phase 70 — admin/circuit_admin only. Backend gates every
      // /admin/machine-clone-templates/* endpoint.
      {
        to: "/admin/machines/templates",
        label: "Template clone",
        testid: "nav-admin-machine-templates",
        icon: <Copy size={16} aria-hidden="true" />,
        permission: "machine_clone_templates:manage",
      },
      {
        to: "/provisioning",
        label: "Provisioning",
        testid: "nav-provisioning",
        icon: <Server size={16} aria-hidden="true" />,
      },
    ],
  },
  {
    title: "Identity",
    items: [
      {
        to: "/users",
        label: "Utenti",
        testid: "nav-users",
        icon: <UserCog size={16} aria-hidden="true" />,
      },
      // Phase 69 — admin/operator user CRUD. Lives under Identity (not
      // Admin) because conceptually it's user/identity management, not
      // backend administration. Per-item permission gate:
      // admin_users:manage (super_admin + circuit_admin own circuit by
      // default; admin EXCLUDED).
      {
        to: "/admin/users",
        label: "Utenti admin",
        testid: "nav-admin-users",
        id: "nav-admin-users",
        icon: <Users size={16} aria-hidden="true" />,
        permission: "admin_users:manage",
      },
      {
        to: "/vpn",
        label: "VPN",
        testid: "nav-vpn",
        icon: <Network size={16} aria-hidden="true" />,
      },
    ],
  },
  {
    title: "Admin",
    admin: true,
    items: [
      {
        to: "/admin/files",
        label: "File Manager",
        testid: "nav-admin-files",
        id: "nav-admin-files",
        icon: <Files size={16} aria-hidden="true" />,
      },
      {
        to: "/admin/audit",
        label: "Audit log",
        testid: "nav-admin-audit",
        id: "nav-admin-audit",
        icon: <Activity size={16} aria-hidden="true" />,
      },
      // Phase 68 Plan 03 — Power Health admin nav.
      {
        to: "/admin/power-health",
        label: "Power Health",
        testid: "nav-admin-power-health",
        id: "nav-admin-power-health",
        icon: <Zap size={16} aria-hidden="true" />,
      },
      {
        to: "/admin/circuits",
        label: "Circuiti",
        testid: "nav-admin-circuits",
        icon: <CircuitBoard size={16} aria-hidden="true" />,
      },
    ],
  },
  {
    title: "Settings",
    items: [
      {
        to: "/settings",
        label: "Impostazioni",
        testid: "nav-settings",
        icon: <Settings size={16} aria-hidden="true" />,
      },
    ],
  },
];

function NavItemLink({ item }: { item: NavItem }) {
  const link = (
    <li>
      <NavLink
        to={item.to}
        end={item.to === "/"}
        id={item.id}
        data-route={item.to}
        data-testid={item.testid}
        className={({ isActive }) =>
          [
            "nav-link flex items-center gap-2 px-3 py-2 text-sm rounded transition-colors",
            "text-[color:var(--color-sidebar-text)] hover:text-white",
            isActive
              ? "bg-[color:var(--color-sidebar-active-bg)] text-white border-l-[3px] border-[color:var(--color-sidebar-active)] pl-[calc(0.75rem-3px)]"
              : "border-l-[3px] border-transparent",
          ].join(" ")
        }
      >
        {item.icon}
        <span>{item.label}</span>
      </NavLink>
    </li>
  );
  if (item.permission) {
    return <Can permission={item.permission}>{link}</Can>;
  }
  return link;
}

export default function Sidebar() {
  return (
    <nav
      id="sidebar"
      data-testid="sidebar"
      className="sidebar col-start-1 row-span-2 flex flex-col bg-[color:var(--color-sidebar)] text-[color:var(--color-sidebar-text)]"
      aria-label="Navigazione principale"
    >
      <div className="sidebar-header flex flex-col items-center justify-center px-4 py-5 border-b border-[color:var(--color-border)]/20">
        <img
          src="/img/eurven/logo_eurven.png"
          alt="Eurven"
          className="logo h-8 w-auto"
        />
        <span className="logo-sub text-[11px] uppercase tracking-wide text-[color:var(--color-sidebar-text-muted)] mt-1">
          Tecnologia sostenibile
        </span>
      </div>

      <div className="nav-links flex-1 overflow-y-auto py-3">
        {sections.map((section) => {
          const inner = (
            <div key={section.title} className="px-3 py-2">
              <h3 className="text-[11px] uppercase tracking-wide text-[color:var(--color-sidebar-text-muted)] mb-1 px-2">
                {section.title}
              </h3>
              <ul className="space-y-0.5 pl-[16px]">
                {section.items.map((item) => (
                  <NavItemLink key={item.to} item={item} />
                ))}
              </ul>
            </div>
          );
          if (section.admin) {
            // Admin section visible only when user has files:read permission.
            // (admin + super_admin both have files:read in ROLE_PERMISSIONS.)
            return (
              <Can key={section.title} permission="files:read">
                {inner}
              </Can>
            );
          }
          return inner;
        })}
      </div>

      <div className="sidebar-footer px-4 py-3 border-t border-[color:var(--color-border)]/20" />
    </nav>
  );
}
